import { GitBranch, Loader2 } from 'lucide-react';
import { Button } from '../components/atoms/Button';

interface Props {
  fromType: 'research' | 'word_edit' | 'draft';
  toMode: 'research' | 'edit' | 'draft';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Modal shown when the user tries to send a message in a mode whose chat_type
 * doesn't match the currently-loaded thread's chat_type. Confirming forks the
 * source thread (copy all messages server-side) into a new chat with the new
 * chat_type; the original stays untouched.
 */
export function ModeSwitchConfirmModal({
  fromType,
  toMode,
  busy,
  onConfirm,
  onCancel,
}: Props): JSX.Element {
  const fromLabel = labelFor(fromType);
  const toLabel = toMode === 'edit' ? 'Edit' : toMode === 'draft' ? 'Draft' : 'Research';

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-foreground/40 backdrop-blur-sm px-6">
      <div className="w-full max-w-sm rounded-lg bg-background border border-border shadow-lg p-4 space-y-3">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <GitBranch className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-foreground">
              Start a new {toLabel} chat?
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Your {fromLabel} thread will stay in History untouched. The new {toLabel}{' '}
              chat will carry all prior messages so you don&apos;t lose context.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-1.5 pt-1">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="default" size="sm" onClick={onConfirm} disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Forking…
              </>
            ) : (
              `Fork to ${toLabel}`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

function labelFor(t: 'research' | 'word_edit' | 'draft'): string {
  if (t === 'word_edit') return 'Edit';
  if (t === 'draft') return 'Draft';
  return 'Research';
}
